import type {
  LoginFormProps,
  ProfileFormValues,
} from './profile-form.interface';
import type { FormikTouched } from 'formik';

import { Form, Formik } from 'formik';
import { memo } from 'react';
import { UPDATE_PROFILE_STATUSES } from 'shared/interfaces/authentication.interface';

import * as utils from '../../../shared/utils';
import { Button } from '../../buttons/button/button';
import { InputField } from '../../fields/input-field/input-field';
import { PasswordField } from '../../fields/password-field/password-field';
import { Spinner } from '../../template/spinner/spinner';

export const ProfileForm = memo(
  ({
    authentication,
    equalPasswordError,
    strongPasswordError,
    onSubmit,
  }: LoginFormProps) => {
    const { user, updateProfileStatus } = authentication;

    const initialValues: ProfileFormValues = {
      email: user?.email || '',
      password: '',
      repeatPassword: '',
    };

    const renderError = (touched: FormikTouched<ProfileFormValues>) => {
      if (equalPasswordError && touched.repeatPassword) {
        return <p className="field-error">Passwords should be equal.</p>;
      }

      if (
        !equalPasswordError &&
        touched.repeatPassword &&
        strongPasswordError
      ) {
        return (
          <p className="field-error">
            Password does not correspond to requirements.
          </p>
        );
      }

      return null;
    };

    if (updateProfileStatus === UPDATE_PROFILE_STATUSES.PENDING) {
      return <Spinner />;
    }

    return (
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={onSubmit}
      >
        {({ errors, touched }) => (
          <Form autoComplete="off" className="form profile-form">
            <div className="form-group">
              <label className="form-label-black">Email</label>
              <InputField
                autocomplete="off"
                name="email"
                validate={utils.validateEmail}
              />
              {errors.email &&
                typeof errors.email === 'string' &&
                touched.email && <p className="field-error">{errors.email}</p>}
            </div>

            <div className="form-group">
              <label className="form-label-black">Password</label>
              <PasswordField name="password" />
            </div>

            <div className="form-group">
              <p className="form-label">
                The password must contain at least one uppercase character, one
                lowercase character, one digit, and one special character.
              </p>
            </div>

            <div className="form-group">
              <label className="form-label-black">Repeat Password</label>
              <PasswordField name="repeatPassword" />
              {renderError(touched)}
            </div>

            <div className="login-form-footer">
              <div />
              <Button text="Save" type="submit" className="button_login" />
            </div>

            {updateProfileStatus === UPDATE_PROFILE_STATUSES.FULFILLED && (
              <p className="field-success">
                The profile is updated successfully!
              </p>
            )}
            {updateProfileStatus === UPDATE_PROFILE_STATUSES.REJECTED && (
              <p className="field-error">Something went wrong!</p>
            )}
          </Form>
        )}
      </Formik>
    );
  },
);

ProfileForm.displayName = 'ProfileForm';
